/**
 * StatusHUD component - Ambient status indicators for active states
 * Dashboard-style indicators for mic, LLM, and modifier states
 */

import { createIcon, type IconAPI } from "@UI/Icon/index.js";
import "./StatusHUD.scss";

export type StatusIndicator = "mic" | "llm" | "shift" | "alt" | "annotate";

export interface StatusHUDConfig {
  readonly initialStates?: Partial<Record<StatusIndicator, boolean>>;
  readonly uiState?: any; // UIStateAPI - typed as any for now to avoid circular dependency
}

export interface StatusHUDAPI {
  readonly getElement: () => HTMLElement;
  readonly setIndicatorState: (
    indicator: StatusIndicator,
    active: boolean
  ) => void;
  readonly getIndicatorState: (indicator: StatusIndicator) => boolean;
  readonly setAllStates: (
    states: Partial<Record<StatusIndicator, boolean>>
  ) => void;
  readonly updateFromUIState: () => void;
  readonly destroy: () => void;
}

/**
 * Creates StatusHUD component with ambient status indicators
 * @param config - Configuration for the status HUD
 * @returns StatusHUD API
 */
export const createStatusHUD = (config: StatusHUDConfig = {}): StatusHUDAPI => {
  const { initialStates = {}, uiState } = config;

  let isDestroyed = false;

  // Initialize indicator states from UI state if available, otherwise use initial states
  const getInitialState = (): Record<StatusIndicator, boolean> => {
    if (uiState) {
      const currentState = uiState.getState();
      return {
        mic: currentState.mic_enabled ?? false,
        llm: currentState.llm_working ?? false,
        shift: currentState.shift_pressed ?? false,
        alt: currentState.alt_pressed ?? false,
        annotate: currentState.pencil_connected ?? false,
      };
    }

    return {
      mic: initialStates.mic ?? false,
      llm: true, // Keep hardcoded for now as requested
      shift: initialStates.shift ?? false,
      alt: initialStates.alt ?? false,
      annotate: initialStates.annotate ?? false,
    };
  };

  let indicatorStates: Record<StatusIndicator, boolean> = getInitialState();
  let indicatorIcons: Map<StatusIndicator, IconAPI> = new Map();
  let indicatorElements: Map<StatusIndicator, HTMLElement> = new Map();

  // Create container element
  const container = document.createElement("div");
  container.className = "status-hud";

  // Indicator configuration
  const indicatorConfig: Record<
    StatusIndicator,
    { icon: string; className: string }
  > = {
    mic: { icon: "interactions_mic", className: "status-indicator--mic" },
    llm: { icon: "llm_llm", className: "status-indicator--llm" },
    shift: { icon: "modifiers_shift", className: "status-indicator--shift" },
    alt: { icon: "modifiers_alt", className: "status-indicator--alt" },
    annotate: {
      icon: "interactions_annotate",
      className: "status-indicator--annotate",
    },
  };

  /**
   * Initialize the status HUD with all indicators
   */
  const initStatusHUD = (): void => {
    try {
      // Create indicators for each status type
      Object.entries(indicatorConfig).forEach(([key, config]) => {
        const indicator = key as StatusIndicator;
        const isActive = indicatorStates[indicator];

        // Create indicator container
        const indicatorElement = document.createElement("div");
        indicatorElement.className = `status-indicator ${config.className} ${
          isActive ? "status-indicator--active" : ""
        }`;

        // Create icon
        const iconAPI = createIcon({
          name: config.icon,
          size: 16, // Small dashboard-style icons
        });

        indicatorElement.appendChild(iconAPI.getElement());
        container.appendChild(indicatorElement);

        // Store references
        indicatorIcons.set(indicator, iconAPI);
        indicatorElements.set(indicator, indicatorElement);
      });
    } catch (error) {
      console.error("Failed to create StatusHUD component:", error);
      throw error;
    }
  };

  /**
   * Set the state of a specific indicator
   */
  const setIndicatorState = (
    indicator: StatusIndicator,
    active: boolean
  ): void => {
    if (isDestroyed) return;

    indicatorStates[indicator] = active;
    const element = indicatorElements.get(indicator);

    if (element) {
      if (active) {
        element.classList.add("status-indicator--active");
      } else {
        element.classList.remove("status-indicator--active");
      }
    }
  };

  /**
   * Get the state of a specific indicator
   */
  const getIndicatorState = (indicator: StatusIndicator): boolean => {
    return indicatorStates[indicator];
  };

  /**
   * Set multiple indicator states at once
   */
  const setAllStates = (
    states: Partial<Record<StatusIndicator, boolean>>
  ): void => {
    if (isDestroyed) return;

    Object.entries(states).forEach(([key, value]) => {
      if (value !== undefined) {
        setIndicatorState(key as StatusIndicator, value);
      }
    });
  };

  /**
   * Update indicator states from UI state
   */
  const updateFromUIState = (): void => {
    if (isDestroyed || !uiState) return;

    const currentState = uiState.getState();

    // Update modifier states from UI state
    setIndicatorState("shift", currentState.shift_pressed);
    setIndicatorState("alt", currentState.alt_pressed);
    setIndicatorState("mic", currentState.mic_enabled);
    setIndicatorState("llm", currentState.llm_working);
    setIndicatorState("annotate", currentState.pencil_connected);
  };

  /**
   * Clean up resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Destroy all icon components
    indicatorIcons.forEach((iconAPI) => {
      iconAPI.destroy();
    });
    indicatorIcons.clear();
    indicatorElements.clear();

    // Remove from DOM if still connected
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };

  // Initialize the component
  initStatusHUD();

  return {
    getElement: () => container,
    setIndicatorState,
    getIndicatorState,
    setAllStates,
    updateFromUIState,
    destroy,
  };
};

/**
 * Creates StatusHUD component and appends to container
 * @param container - Container element to append to
 * @param config - Configuration for the status HUD
 * @returns StatusHUD API
 */
export const createStatusHUDInContainer = (
  container: HTMLElement,
  config: StatusHUDConfig = {}
): StatusHUDAPI => {
  const statusHUD = createStatusHUD(config);
  container.appendChild(statusHUD.getElement());
  return statusHUD;
};
